<template>
    <page>
        <fast-table
            id="payroll-start-table"
            ref="table"
            recordLabel="payroll runs"
            :sections="sections"
            :headers="headers"
            :data="tableData"
            :use-date-filters="true"
            :show-filter="true"
            :show-search="true"
            :show-more-actions="false"
            :subrows-expandable="true"
        >
            <template v-slot:table-actions>

                <button type="button" class="btn btn-primary" @click.prevent="createRunsModalOpen = true" v-if="$store.state.user.is_superuser">
                    <i class="far fa-fw fa-plus"></i> <span class="btn-text">Create Runs (Admin)</span>
                </button>
            </template>
        </fast-table>

        <paygroup-sidebar
            v-if="currentRunId || (currentCollectionId && currentCollectionStartDate)"
            :client="client"
            :run-id="currentRunId"
            :collection-id="currentCollectionId"
            :collection-start-date="currentCollectionStartDate"
            @close="onCloseSidebar"
            @updated="getData"
        />

        <admin-payroll-modal v-if="createRunsModalOpen" :client="client" @updated="getData" @close="createRunsModalOpen = false"/>
    </page>
</template>

<script>
import Page from '@/components/Page'
import FastTable from '@/components/FastTable'
import PaygroupSidebar from './components/PaygroupSidebar'
import AdminPayrollModal from './components/AdminPayrollModal'

export default {
    components: {Page, FastTable, PaygroupSidebar, AdminPayrollModal},
    props: ['client'],
    data() {
        return {
            paygroups: [],
            collections: [],
            runs: [],
            currentRunId: null,
            currentCollectionId: null,
            currentCollectionStartDate: null,
            createRunsModalOpen: false,
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Payroll')
        this.getData()
    },
    computed: {
        current() {
            const today = new Date()
            return this.runs.filter((r) => {
                const endDate = new Date(r.pay_period_end_date)
                return (['not-started', 'in-progress', 'ready-for-approval'].includes(r.status) && endDate < today) // TODO KB PG is this the correct logic for current runs?
            })
        },
        upcoming() {
            const today = new Date()
            return this.runs.filter((r) => {
                const endDate = new Date(r.pay_period_end_date)
                return (['not-started', 'in-progress', 'ready-for-approval'].includes(r.status) && endDate >= today)
            })
        },
        completed() {
            return this.runs.filter((r) => r.status == 'completed')
        },
        canceled() {
            return this.runs.filter((r) => r.status == 'canceled')
        },
        sections() {
            const sections = [
                {title: 'Current', defaultIsClosed: false, id: 'current', hasDateFilter: false, showSubrowsAnchorCol: true},
                {title: 'Upcoming', defaultIsClosed: false, id: 'upcoming', hasDateFilter: false, showSubrowsAnchorCol: true},
                {title: 'Completed', defaultIsClosed: true, id: 'completed', hasDateFilter: true},
                {title: 'Canceled', defaultIsClosed: true, id: 'hired', hasDateFilter: true},
            ]
            return sections
        },
        headers() {
            const headers = [
                {label: 'Pay Group', classes: '', isSearchable: true, type: "action"},
                {label: '', classes: 'cw-10 align-middle', isSearchable: false, isFilterable: false, type: "action"}, // TODO KB PG figure out how to make this part of the anchor col
                {label: 'Warnings', classes: 'cw-6 align-middle', isSearchable: false, isFilterable: true, type: 'warning'},
                {label: 'Alerts', classes: 'cw-5 align-middle', isSearchable: false, isFilterable: true, type: 'warning'},
                {label: 'Status', classes: 'cw-6 text-semibold align-middle', isSearchable: true, isFilterable: true},
                {label: 'Process By', classes: 'cw-12 align-middle', isSearchable: false, type: 'datetime', format: 'ddd MM/DD/YY h:mm a', defaultSort: true, defaultSortIsDesc: false},
                {label: 'Check Date', classes: 'cw-8 align-middle', isSearchable: false, isFilterable: false, type: 'date'},
                {label: 'Pay Period', classes: 'cw-14 align-middle', isSearchable: false, isFilterable: false},
            ]
            return headers
        },
        tableData() {
            return this.makeTableData()
        }
    },
    methods: {
        goToPaygrid(run) {
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/paygrid/paygroup/${run.paygroup_id}/run/${run.id}`,
            })
        },
        goToPaygridCollection(run) {
            const paygroup = this.paygroups.find(pg => pg.id == run.paygroup_id)
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/paygrid/collection/${paygroup.collection_id}`,
                query: {
                    pay_period_start_date: run.pay_period_start_date
                }
            })
        },
        openSidebar(runId) {
            this.currentCollectionId = null
            this.currentCollectionStartDate = null
            this.currentRunId = runId
        },
        openCollectionSidebar(collectionId, startDate) {
            this.currentRunId = null
            this.currentCollectionId = collectionId
            this.currentCollectionStartDate = startDate
        },
        onCloseSidebar() {
            this.currentRunId = null
            this.currentCollectionId = null
            this.currentCollectionStartDate = null
        },
        getStatusPretty(status) {
            return {
                'not-started': 'Not Started',
                'in-progress': 'In Progress', // TODO KB PG handle 'Waiting for Completion' pseudo-status
                'ready-for-approval': 'Ready for Approval', // TODO KB PG add logic to show 'Ready for Approval' for approver or 'Waiting for Completion' otherwise
                'completed': 'Completed',
                'canceled': 'Canceled'
            }[status]
        },
        getActionText(status) {
            return {
                'not-started': 'Start Payroll',
                'in-progress': 'Resume Payroll',
                'ready-for-approval': 'View Pay Grid', // TODO KB PG add logic to show 'Start Payroll' for approver or 'View Pay Grid' otherwise
                'completed': 'View Pay Grid',
                'canceled': ''
            }[status]
        },
        getData() {
            const url = `/clients/${this.client.id}/payroll`

            this.$api.get(url).then(resp => {
                this.collections = resp.paygroup_collections
                this.paygroups = resp.paygroups
                this.runs = resp.paygroup_runs
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.$emit('no-access')
                    return
                }
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        makeTableData() {
            const tableData = []
            const sections = [
                this.current,
                this.upcoming,
                this.completed,
                this.canceled,
            ]

            const paygroupsById = Object.fromEntries(this.paygroups.map(pg => [pg.id, pg]))

            sections.forEach(section => {
                const sectionResult = []
                const collectionsById = Object.fromEntries(
                    this.collections.map(c => [c.id, { ...c, subrows: [], seenStatuses: new Set() }])
                )
                // TODO KB PG this would have a display issue if multiple runs for the same paygroup are in the same section at a given time. If that case is possible, we need to group by collection.id + run.pay_period_start_date key

                section.forEach(run => {
                    const paygroup = paygroupsById[run.paygroup_id]
                    const collectionId = paygroup.collection_id
                    const runStatus = run.status  // TODO KB PG add logic to use run.location_statuses to determinue status if user does not have access to paygroup's whole company (it's fine if they only have access to a position in location)
                    const statusPretty = this.getStatusPretty(runStatus)
                    const actionText = this.getActionText(runStatus)
                    let payPeriodStr = ''
                    if (run.pay_period_start_date && run.pay_period_end_date) {
                        payPeriodStr = `${this.$filters.dateshort(run.pay_period_start_date)} - ${this.$filters.dateshort(run.pay_period_end_date)}`
                    }

                    // for 'current' and 'upcoming' runs, group into collections using subrows
                    if (['not-started', 'in-progress', 'ready-for-approval'].includes(run.status) && collectionId && collectionsById[collectionId]) {
                        const collection = collectionsById[collectionId]
                        if (!collection.firstRun) {
                            collection.firstRun = run
                        }
                        collection.seenStatuses.add(runStatus)
                        collection.subrows.push([
                            {
                                value: `${paygroup.name} ${paygroup.company.name}`,
                                html: `${paygroup.name}<br/><span class='text-muted text-small text-regular'>${paygroup.company.name}</span>`,
                                callback: (val) => {
                                    this.openSidebar(val)
                                },
                                callbackValue: run.id,
                            },
                            {
                                value: '',
                                html: `<a class='btn btn-outline-primary'>${actionText}</a>`,
                                callback: (run) => {
                                    this.goToPaygrid(run)
                                },
                                callbackValue: run,
                            },
                            [], // warnings
                            [], // alerts
                            statusPretty ,
                            { value: '', _meta: {type: 'string'}},
                            { value: '', _meta: {type: 'string'}},
                            { value: '', _meta: {type: 'string'}},
                        ])
                    } else {
                        // Run is not in a collection, push directly
                        const btnClass = ['not-started', 'in-progress'].includes(run.status) ? 'btn-primary' : 'btn-outline-primary' // TODO KB PG this should also be primary for approver in 'ready-for-approval' status
                        sectionResult.push({
                            id: run.id,
                            object: run,
                            cells: [
                                {
                                    value: `${paygroup.name} ${paygroup.company.name}`,
                                    html: `${paygroup.name}<br/><span class='text-muted text-small text-regular'>${paygroup.company.name}</span>`,
                                    callback: (val) => {
                                        this.openSidebar(val)
                                    },
                                    callbackValue: run.id,
                                },
                                {
                                    value: '',
                                    html: `<a class='btn ${btnClass}'>${actionText}</a>`,
                                    callback: (run) => {
                                        this.goToPaygrid(run)
                                    },
                                    callbackValue: run,
                                },
                                [], // warnings
                                [], // alerts
                                statusPretty,
                                run.process_by_time,
                                run.check_date,
                                payPeriodStr
                            ],
                        })
                    }
                })

                // Now add the collections that have runs
                for (const collection of Object.values(collectionsById)) {
                    if (!collection.subrows.length) {
                        continue
                    }
                    let collectionStatus = ''
                    if (collection.seenStatuses.has('in-progress')) {
                        collectionStatus = 'in-progress'
                    } else if (collection.seenStatuses.has('not-started')) {
                        collectionStatus = 'not-started'
                    } else if (collection.seenStatuses.has('ready-for-approval')) {
                        collectionStatus = 'ready-for-approval'
                    }
                    const statusPretty = this.getStatusPretty(collectionStatus)
                    const actionText = this.getActionText(collectionStatus)
                    let payPeriodStr = ''
                    if (collection.firstRun.pay_period_start_date && collection.firstRun.pay_period_end_date) {
                        payPeriodStr = `${this.$filters.dateshort(collection.firstRun.pay_period_start_date)} - ${this.$filters.dateshort(collection.firstRun.pay_period_end_date)}`
                    }
                    sectionResult.unshift({
                        id: collection.id,
                        object: collection,
                        cells: [
                            {
                                value: collection.name,
                                html: `<a class="uppercase">${collection.name} (${collection.subrows.length})</a>`,
                                callback: (val) => {
                                    this.openCollectionSidebar(...val)
                                },
                                callbackValue: [collection.id, collection.firstRun.pay_period_start_date],
                            },
                            {
                                value: '',
                                html: `<a class='btn btn-primary'>${actionText}</a>`,
                                callback: (run) => {
                                    this.goToPaygridCollection(run)
                                },
                                callbackValue: collection.firstRun,
                            },
                            [], // warnings
                            [], // alerts
                            statusPretty,
                            collection.firstRun.process_by_time,
                            collection.firstRun.check_date,
                            payPeriodStr
                        ],
                        subrows: collection.subrows,
                        isExpandable: true,
                        defaultIsClosed: true,
                    })
                }

                tableData.push(sectionResult)
            })

            return tableData
        },
    },
}
</script>

<style scoped>
    #payroll-start-table :deep(.tr-subrow .td) {
        border-top: 0;
    }
</style>
